/**
 * Memoized risk-area tables for the worksheet editor.
 *
 * Expanding a row and typing in a comment box used to call setState on the
 * whole worksheet, so every area, every input, and every TipTap editor
 * reconciled together. Expand state now lives on the row, and the rich-text
 * fields keep keystrokes locally until blur / save.
 */

import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import TimelineIcon from "@mui/icons-material/Timeline";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Chip from "@mui/material/Chip";

import SkillDocumentCommentBox from "../../../../components/SkillDocumentCommentBox/SkillDocumentCommentBox";
import { isRichTextEmpty } from "../../../../components/SkillDocumentCommentBox/utils";
import {
  Risk,
  RiskArea,
  RiskSnapshot,
  RiskStatus,
} from "../Types/RiskManagementTypes";
import RiskScoreBadge, {
  computeExposure,
  levelFromExposure,
} from "./RiskScoreBadge";

export interface EditableRiskRow {
  risk: Risk;
  snapshot: RiskSnapshot | null;
  draftImpact: number;
  draftProbability: number;
  draftMitigation: string;
  draftTargetDate: string;
  draftStatus: RiskStatus;
  draftComments: string;
  draftRatingFactors: string;
  draftScenarioJustification: string;
  isDirty: boolean;
}

export const TEXT_FIELDS = [
  "draftRatingFactors",
  "draftScenarioJustification",
  "draftMitigation",
] as const;

export type RiskTextField = (typeof TEXT_FIELDS)[number];

export const textFlushKey = (riskId: string, field: RiskTextField) =>
  `${riskId}::${field}`;

/** TipTap's empty document is `<p></p>`, which must not count as a user edit. */
export const canonicalEditorHtml = (html: string) =>
  isRichTextEmpty(html) ? "" : html;

const sameEditorHtml = (stored: string, live: string) =>
  stored === live || canonicalEditorHtml(stored) === canonicalEditorHtml(live);

const STATUS_OPTIONS: { value: RiskStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "mitigated", label: "Mitigated" },
  { value: "closed", label: "Closed" },
  { value: "reappeared", label: "Reappeared" },
];

const EXPAND_MS = 260;
const EXPAND_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * Return the same row reference when the patch does not change stored
 * fields, so a blur that re-emits the current HTML does not re-render.
 * An empty patch marks the row dirty once (first keystroke).
 */
export const patchEditableRow = (
  row: EditableRiskRow,
  patch: Partial<EditableRiskRow>
): EditableRiskRow => {
  const keys = Object.keys(patch) as (keyof EditableRiskRow)[];
  if (keys.length === 0) {
    return row.isDirty ? row : { ...row, isDirty: true };
  }
  const changed = keys.some((key) => !Object.is(patch[key], row[key]));
  if (!changed) return row;
  return { ...row, ...patch, isDirty: true };
};

export const collectDirtyRows = (
  grouped: Record<string, EditableRiskRow[]>
): EditableRiskRow[] => {
  const out: EditableRiskRow[] = [];
  Object.values(grouped).forEach((rows) => {
    rows.forEach((row) => {
      if (row.isDirty) out.push(row);
    });
  });
  return out;
};

/** Overlay live editor HTML onto row state. Returns the same reference when nothing changed. */
export const mergeLiveEditorText = (
  grouped: Record<string, EditableRiskRow[]>,
  registry: Map<string, () => string>
): Record<string, EditableRiskRow[]> => {
  if (registry.size === 0) return grouped;
  let changed = false;
  const next: Record<string, EditableRiskRow[]> = { ...grouped };
  const cloned = new Set<string>();

  Object.entries(grouped).forEach(([areaId, rows]) => {
    rows.forEach((row, idx) => {
      const patch: Partial<EditableRiskRow> = {};
      let rowChanged = false;
      TEXT_FIELDS.forEach((field) => {
        const read = registry.get(textFlushKey(row.risk.id, field));
        if (!read) return;
        let html = "";
        try {
          html = canonicalEditorHtml(read());
        } catch {
          return;
        }
        if (sameEditorHtml(row[field], html)) return;
        patch[field] = html;
        rowChanged = true;
      });
      if (!rowChanged) return;
      if (!cloned.has(areaId)) {
        next[areaId] = rows.slice();
        cloned.add(areaId);
      }
      next[areaId][idx] = { ...row, ...patch, isDirty: true };
      changed = true;
    });
  });

  return changed ? next : grouped;
};

const areaStats = (rows: EditableRiskRow[]) => {
  if (!rows.length) return { avg: 0, level: "LOW" as const, total: 0 };
  let total = 0;
  rows.forEach((row) => {
    total += computeExposure(row.draftImpact, row.draftProbability);
  });
  const avg = total / rows.length;
  return { avg, level: levelFromExposure(avg), total: rows.length };
};

interface BoundedNumberInputProps {
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  width?: number;
  onCommit: (next: number) => void;
}

/**
 * Local string while typing; the parent hears a clamped integer on blur.
 * Committing on each keystroke re-rendered the worksheet and dropped the
 * second digit of "10".
 */
const BoundedNumberInput: React.FC<BoundedNumberInputProps> = ({
  value,
  min,
  max,
  disabled,
  width = 50,
  onCommit,
}) => {
  const [draft, setDraft] = useState<string>(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Math.round(Number(draft));
    const safe = Number.isFinite(parsed)
      ? Math.min(max, Math.max(min, parsed))
      : min;
    if (safe !== value) onCommit(safe);
    setDraft(String(safe));
  };

  return (
    <TextField
      size="small"
      type="number"
      value={draft}
      disabled={disabled}
      inputProps={{ min, max, step: 1, style: { width } }}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" || /^-?\d+$/.test(v)) setDraft(v);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit();
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
};

interface RiskWorksheetRowProps {
  areaId: string;
  row: EditableRiskRow;
  canEdit: boolean;
  onUpdate: (
    areaId: string,
    riskId: string,
    patch: Partial<EditableRiskRow>
  ) => void;
  onCommitText: (areaId: string, riskId: string) => void;
  onClose: (risk: Risk) => void;
  onOpenHistory: (riskId: string) => void;
  onOpenAudit: (riskId: string) => void;
  registerFlush: (key: string, getter: (() => string) | null) => void;
}

const RiskWorksheetRow = memo(function RiskWorksheetRow({
  areaId,
  row,
  canEdit,
  onUpdate,
  onCommitText,
  onClose,
  onOpenHistory,
  onOpenAudit,
  registerFlush,
}: RiskWorksheetRowProps) {
  const riskId = row.risk.id;
  const [expanded, setExpanded] = useState(false);
  const [detailsClosed, setDetailsClosed] = useState(true);
  const dirtyRef = React.useRef(row.isDirty);
  dirtyRef.current = row.isDirty;

  const markDirty = useCallback(() => {
    if (dirtyRef.current) return;
    dirtyRef.current = true;
    // Defer so the open TipTap transaction finishes before React re-renders.
    queueMicrotask(() => onUpdate(areaId, riskId, {}));
  }, [onUpdate, areaId, riskId]);

  const onRatingChange = useCallback(
    (html: string) =>
      onUpdate(areaId, riskId, {
        draftRatingFactors: canonicalEditorHtml(html),
      }),
    [onUpdate, areaId, riskId]
  );
  const onScenarioChange = useCallback(
    (html: string) =>
      onUpdate(areaId, riskId, {
        draftScenarioJustification: canonicalEditorHtml(html),
      }),
    [onUpdate, areaId, riskId]
  );
  const onMitigationChange = useCallback(
    (html: string) =>
      onUpdate(areaId, riskId, { draftMitigation: canonicalEditorHtml(html) }),
    [onUpdate, areaId, riskId]
  );

  const bindRating = useCallback(
    (getter: (() => string) | null) =>
      registerFlush(textFlushKey(riskId, "draftRatingFactors"), getter),
    [registerFlush, riskId]
  );
  const bindScenario = useCallback(
    (getter: (() => string) | null) =>
      registerFlush(textFlushKey(riskId, "draftScenarioJustification"), getter),
    [registerFlush, riskId]
  );
  const bindMitigation = useCallback(
    (getter: (() => string) | null) =>
      registerFlush(textFlushKey(riskId, "draftMitigation"), getter),
    [registerFlush, riskId]
  );

  const toggleExpanded = () => {
    if (expanded) {
      onCommitText(areaId, riskId);
      setExpanded(false);
      return;
    }
    setDetailsClosed(false);
    setExpanded(true);
  };

  const exposure = computeExposure(row.draftImpact, row.draftProbability);
  const level = levelFromExposure(exposure);

  const editors: {
    label: string;
    placeholder: string;
    value: string;
    onChange: (html: string) => void;
    bind: (getter: (() => string) | null) => void;
  }[] = [
    {
      label: "Factors for rating Impact & Probability",
      placeholder: "Enter factors...",
      value: row.draftRatingFactors,
      onChange: onRatingChange,
      bind: bindRating,
    },
    {
      label: "Specify Project Scenario justifying the impact and probability",
      placeholder: "Describe the scenario...",
      value: row.draftScenarioJustification,
      onChange: onScenarioChange,
      bind: bindScenario,
    },
    {
      label: "Mitigation plan",
      placeholder: "Mitigation plan...",
      value: row.draftMitigation,
      onChange: onMitigationChange,
      bind: bindMitigation,
    },
  ];

  return (
    <>
      <TableRow
        hover
        sx={{ "& > *": { borderBottom: expanded ? "unset" : undefined } }}
      >
        <TableCell sx={{ width: 40, verticalAlign: "top" }}>
          <IconButton
            size="small"
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse risk details" : "Expand risk details"}
            onClick={toggleExpanded}
          >
            <KeyboardArrowDownIcon
              fontSize="small"
              sx={{
                transition: `transform ${EXPAND_MS}ms ${EXPAND_EASING}`,
                transform: expanded ? "rotate(180deg)" : "none",
              }}
            />
          </IconButton>
        </TableCell>
        <TableCell sx={{ minWidth: 180, verticalAlign: "top" }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.risk.contributor_name ?? "—"}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 90 }}>
          <BoundedNumberInput
            value={row.draftImpact}
            min={1}
            max={10}
            disabled={!canEdit}
            onCommit={(next) =>
              onUpdate(areaId, riskId, { draftImpact: next })
            }
          />
        </TableCell>
        <TableCell sx={{ width: 90 }}>
          <BoundedNumberInput
            value={row.draftProbability}
            min={1}
            max={10}
            disabled={!canEdit}
            onCommit={(next) =>
              onUpdate(areaId, riskId, { draftProbability: next })
            }
          />
        </TableCell>
        <TableCell sx={{ width: 140 }}>
          <RiskScoreBadge level={level} exposure={exposure} />
        </TableCell>
        <TableCell sx={{ width: 150 }}>
          <TextField
            size="small"
            type="date"
            value={row.draftTargetDate}
            disabled={!canEdit}
            onChange={(e) =>
              onUpdate(areaId, riskId, { draftTargetDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />
        </TableCell>
        <TableCell sx={{ width: 150 }}>
          <FormControl size="small" fullWidth disabled={!canEdit}>
            <Select
              value={row.draftStatus}
              onChange={(e) =>
                onUpdate(areaId, riskId, {
                  draftStatus: e.target.value as RiskStatus,
                })
              }
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell sx={{ width: 110, verticalAlign: "top" }}>
          <Stack direction="row" spacing={0}>
            <Tooltip title="Trend">
              <IconButton size="small" onClick={() => onOpenHistory(riskId)}>
                <TimelineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Audit log">
              <IconButton size="small" onClick={() => onOpenAudit(riskId)}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canEdit && (
              <Tooltip title="Close risk">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onClose(row.risk)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow sx={detailsClosed ? { visibility: "collapse" } : undefined}>
        <TableCell
          colSpan={8}
          sx={{
            py: 0,
            borderBottom: detailsClosed ? 0 : undefined,
          }}
        >
          <Collapse
            in={expanded}
            timeout={EXPAND_MS}
            easing={EXPAND_EASING}
            unmountOnExit
            onExited={() => setDetailsClosed(true)}
          >
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2}>
                {editors.map((editor) => (
                  <Grid item xs={12} md={4} key={editor.label}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ mb: 0.5, display: "block" }}
                    >
                      {editor.label}
                    </Typography>
                    <Box sx={{ minHeight: 132 }}>
                      <SkillDocumentCommentBox
                        compact
                        hideTableControls
                        commitOnBlur
                        value={editor.value}
                        onChange={editor.onChange}
                        onEditing={canEdit ? markDirty : undefined}
                        bindHtmlGetter={editor.bind}
                        disabled={!canEdit}
                        placeholder={editor.placeholder}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});

interface RiskAreaPanelProps {
  area: RiskArea;
  rows: EditableRiskRow[];
  canEdit: boolean;
  onUpdate: RiskWorksheetRowProps["onUpdate"];
  onCommitText: RiskWorksheetRowProps["onCommitText"];
  onClose: (risk: Risk) => void;
  onOpenHistory: (riskId: string) => void;
  onOpenAudit: (riskId: string) => void;
  onAdd: (areaId: string) => void;
  registerFlush: RiskWorksheetRowProps["registerFlush"];
}

const RiskAreaPanel = memo(function RiskAreaPanel({
  area,
  rows,
  canEdit,
  onUpdate,
  onCommitText,
  onClose,
  onOpenHistory,
  onOpenAudit,
  onAdd,
  registerFlush,
}: RiskAreaPanelProps) {
  const stats = useMemo(() => areaStats(rows), [rows]);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ flex: 1 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {area.name}
          </Typography>
          <Chip size="small" label={`${rows.length} risk(s)`} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Area avg
          </Typography>
          <RiskScoreBadge level={stats.level} exposure={stats.avg} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Risk Contributors</TableCell>
                <TableCell>Impact</TableCell>
                <TableCell>Probability</TableCell>
                <TableCell>Level / Exposure</TableCell>
                <TableCell>Target date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      align="center"
                    >
                      No risks in this area yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <RiskWorksheetRow
                    key={row.risk.id}
                    areaId={area.id}
                    row={row}
                    canEdit={canEdit}
                    onUpdate={onUpdate}
                    onCommitText={onCommitText}
                    onClose={onClose}
                    onOpenHistory={onOpenHistory}
                    onOpenAudit={onOpenAudit}
                    registerFlush={registerFlush}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {canEdit && (
          <Box sx={{ mt: 1.5, textAlign: "right" }}>
            <Button
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => onAdd(area.id)}
            >
              Add risk in {area.name}
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
});

export default RiskAreaPanel;
